import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import packageJson from '../package.json';
import { buildResearchRouteUrl, parseResearchRoute, type ResearchTab } from './routing';
const version = packageJson.version;
import {
  Activity,
  ArrowLeft,
  BarChart3,
  CheckSquare,
  ChevronDown,
  Database,
  FileText,
  Info,
  KeyRound,
  LogIn,
  LogOut,
  Loader2,
  Lock,
  RefreshCw,
  Search,
  Server,
  Shield,
  Trash2,
  TrendingUp,
  Upload,
  UserCog,
  X,
  Globe,
  Key,
  User,
} from 'lucide-react';
import './styles.css';

declare const process: { env?: Record<string, string | undefined> } | undefined;

type Tab = ResearchTab;

const BULK_DELETE_TABS: Tab[] = ['backtests', 'portfolios', 'optimizations', 'prices', 'artifacts', 'keys'];

const emptyRowSelections = (): Record<Tab, Set<string>> => ({
  backtests: new Set(),
  portfolios: new Set(),
  optimizations: new Set(),
  prices: new Set(),
  artifacts: new Set(),
  users: new Set(),
  keys: new Set(),
  mcp: new Set(),
  audit: new Set(),
});

const tabs: Array<{ id: Tab; label: string; icon: React.ElementType }> = [
  { id: 'backtests', label: 'Backtests', icon: Activity },
  { id: 'portfolios', label: 'Portfolios', icon: BarChart3 },
  { id: 'optimizations', label: 'Optimizations', icon: Database },
  { id: 'prices', label: 'Market Data', icon: TrendingUp },
  { id: 'artifacts', label: 'Artifacts', icon: FileText },
  { id: 'users', label: 'Users', icon: UserCog },
  { id: 'keys', label: 'API Security', icon: KeyRound },
  { id: 'mcp', label: 'MCP Access', icon: Server },
  { id: 'audit', label: 'Audit Log', icon: Shield },
];

const AVAILABLE_SCOPES = ['read:artifacts', 'write:artifacts', 'delete:artifacts', 'read:mcp', 'admin:security'];

const ALL_COLUMNS: Record<Tab, string[]> = {
  backtests: ['id', 'user_id', 'timestamp', 'strategy', 'symbol', 'sec_type', 'data_source', 'start_date', 'end_date', 'timeframe', 'sl_atr_mult', 'entry_type', 'exit_type', 'total_return', 'sharpe'],
  portfolios: ['id', 'user_id', 'timestamp', 'name', 'start_date', 'end_date', 'initial_capital', 'total_return', 'sharpe'],
  optimizations: ['id', 'user_id', 'timestamp', 'strategy', 'symbol', 'start_date', 'end_date', 'timeframe', 'entry_type', 'exit_type', 'optimize_for', 'combinations'],
  prices: ['symbol', 'sec_type', 'data_type', 'methodology', 'value_unit', 'con_id', 'stk_type', 'indexes', 'exchange', 'data_source', 'timeframe', 'multiplier', 'start', 'end', 'bars', 'updated_at'],
  artifacts: ['id', 'user_id', 'updated_at', 'title', 'artifact_type', 'tags'],
  keys: ['id', 'name', 'key_prefix', 'scopes', 'user_id', 'is_active', 'last_used_at', 'created_at', 'revoked_at'],
  users: ['user_id', 'username', 'role', 'scopes', 'is_active'],
  mcp: ['id', 'client_name', 'key_prefix', 'scopes', 'is_active', 'last_used_at', 'created_at'],
  audit: ['id', 'created_at', 'actor_type', 'actor_id', 'action', 'resource_type', 'resource_id', 'detail'],
};

interface CustomSelectProps {
  label: string;
  shortLabel?: string;
  value: string;
  shortValue?: string;
  options: string[];
  onChange: (value: string) => void;
}

const CustomSelect: React.FC<CustomSelectProps> = ({ label, shortLabel, value, shortValue, options, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const displayLabel = isOpen ? label : (shortLabel || label);
  const displayValue = isOpen ? (value || 'All') : (shortValue || value || 'All');

  return (
    <div className="custom-select-container" ref={ref}>
      <div className="custom-select-trigger" onClick={() => setIsOpen(!isOpen)} title={`${label}: ${value || 'All'}`}>
        <span className="custom-select-label">{displayLabel}:</span>
        <span className="custom-select-value">{displayValue}</span>
        <ChevronDown size={13} className={`custom-select-chevron ${isOpen ? 'open' : ''}`} />
      </div>
      {isOpen && (
        <div className="custom-select-options">
          <div
            className={`custom-select-option ${value === '' ? 'selected' : ''}`}
            onClick={() => {
              onChange('');
              setIsOpen(false);
            }}
          >
            All
          </div>
          {options.map(opt => (
            <div
              key={opt}
              className={`custom-select-option ${value === opt ? 'selected' : ''}`}
              onClick={() => {
                onChange(opt);
                setIsOpen(false);
              }}
            >
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface MultiSelectProps {
  label: string;
  shortLabel?: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}

const MultiSelect: React.FC<MultiSelectProps> = ({ label, shortLabel, value, options, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const selectedList = value ? value.split(',').map(s => s.trim()).filter(Boolean) : [];

  const handleToggle = (opt: string) => {
    let nextList: string[];
    if (selectedList.includes(opt)) {
      nextList = selectedList.filter(x => x !== opt);
    } else {
      nextList = [...selectedList, opt];
    }
    onChange(nextList.join(','));
  };

  const handleClearAll = () => {
    onChange('');
  };

  const maxDisplay = 100;
  const q = search.toLowerCase().trim();
  const matching = useMemo(() => {
    return q ? options.filter(opt => opt.toLowerCase().includes(q)) : options;
  }, [options, q]);

  const displayedOptions = useMemo(() => {
    if (matching.length <= maxDisplay) return matching;
    const selectedSet = new Set(selectedList);
    const selectedMatches = matching.filter(opt => selectedSet.has(opt));
    const unselectedMatches = matching.filter(opt => !selectedSet.has(opt));
    return [...selectedMatches, ...unselectedMatches.slice(0, Math.max(0, maxDisplay - selectedMatches.length))];
  }, [matching, selectedList]);

  const renderClosedValue = () => {
    if (selectedList.length === 0) return 'All';
    if (selectedList.length === options.length) return 'All';
    if (selectedList.length === 1) return selectedList[0];
    return `${selectedList.length} sel`;
  };

  const displayLabel = isOpen ? label : (shortLabel || label);
  const displayValue = isOpen 
    ? (selectedList.length === 0 ? 'All' : selectedList.length === options.length ? 'All' : selectedList.length === 1 ? selectedList[0] : `${selectedList.length} Selected`)
    : renderClosedValue();

  return (
    <div className="custom-select-container" ref={ref}>
      <div 
        className="custom-select-trigger" 
        onClick={() => {
          setSearch('');
          setIsOpen(open => !open);
        }}
        title={`${label}: ${selectedList.length === 0 ? 'All' : selectedList.join(', ')}`}
      >
        <span className="custom-select-label">{displayLabel}:</span>
        <span className="custom-select-value">{displayValue}</span>
        <ChevronDown size={13} className={`custom-select-chevron ${isOpen ? 'open' : ''}`} />
      </div>
      {isOpen && (
        <div className="custom-select-options" style={{ maxHeight: '280px', overflowY: 'auto', padding: '4px 0' }}>
          <div style={{ padding: '0 8px 4px 8px' }}>
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: '100%',
                background: '#0f172a',
                border: '1px solid #334155',
                borderRadius: '4px',
                padding: '4px 8px',
                color: '#f8fafc',
                fontSize: '0.8rem',
                minHeight: '28px',
                height: '28px',
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div
            className="custom-select-option"
            onClick={() => {
              if (selectedList.length > 0) {
                handleClearAll();
              } else {
                if (options.length > 50) {
                  onChange('');
                } else {
                  onChange(options.join(','));
                }
              }
            }}
            style={{ padding: '4px 12px', fontSize: '0.8rem', fontWeight: 600, color: '#38bdf8' }}
          >
            {selectedList.length > 0 ? 'Clear Selection (All)' : (options.length > 50 ? 'All' : 'Select All')}
          </div>
          <div style={{ borderBottom: '1px solid #334155', margin: '2px 0' }} />
          {displayedOptions.map(opt => {
            const isSelected = selectedList.includes(opt);
            return (
              <div
                key={opt}
                className={`custom-select-option ${isSelected ? 'selected' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggle(opt);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '4px 12px',
                  fontSize: '0.8rem',
                  background: isSelected ? '#334155' : 'transparent',
                }}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  readOnly
                  style={{ cursor: 'pointer', accentColor: '#0284c7', margin: 0 }}
                />
                <span>{opt}</span>
              </div>
            );
          })}
          {matching.length > displayedOptions.length && (
            <div style={{ padding: '6px 12px', fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic', textAlign: 'center' }}>
              Showing {displayedOptions.length} of {matching.length} options. Type to search.
            </div>
          )}
          {matching.length === 0 && (
            <div style={{ padding: '8px', color: '#94a3b8', fontSize: '0.8rem', textAlign: 'center' }}>
              No matches found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const getRowId = (currentTab: Tab, item: any): string => {
  if (currentTab === 'prices') {
    return `${item.symbol}:${item.sec_type || 'STK'}:${item.data_source || 'RESEARCH'}:${item.timeframe || 'day'}:${item.multiplier ?? 1}:${item.data_type || 'TRADES'}`;
  }
  return item.id !== undefined && item.id !== null ? String(item.id) : '';
};

const mergePriceItems = (rawItems: any[]): any[] => {
  const map = new Map<string, any>();
  for (const item of rawItems) {
    if (!item) continue;
    const id = getRowId('prices', item);
    if (!map.has(id)) {
      map.set(id, { ...item });
    } else {
      const existing = map.get(id);
      const existingIndexes = Array.isArray(existing.indexes)
        ? existing.indexes.map(String)
        : typeof existing.indexes === 'string'
        ? existing.indexes.split(',').map((s: string) => s.trim()).filter(Boolean)
        : [];
      const itemIndexes = Array.isArray(item.indexes)
        ? item.indexes.map(String)
        : typeof item.indexes === 'string'
        ? item.indexes.split(',').map((s: string) => s.trim()).filter(Boolean)
        : [];
      const combined = Array.from(new Set([...existingIndexes, ...itemIndexes])).sort();
      existing.indexes = combined.length > 0 ? combined.join(', ') : (existing.indexes || item.indexes);
      existing.bars = Math.max(existing.bars || 0, item.bars || 0);
      if (item.start && (!existing.start || item.start < existing.start)) existing.start = item.start;
      if (item.end && (!existing.end || item.end > existing.end)) existing.end = item.end;
      if (item.updated_at && (!existing.updated_at || item.updated_at > existing.updated_at)) existing.updated_at = item.updated_at;
      if (item.con_id && !existing.con_id) existing.con_id = item.con_id;
      if (item.stk_type && !existing.stk_type) existing.stk_type = item.stk_type;
      if (item.exchange && !existing.exchange) existing.exchange = item.exchange;
    }
  }
  return Array.from(map.values());
};

interface ScopeSelectorProps {
  currentScopes: string;
  onChange: (scopes: string) => void;
}

function ScopeSelector({ currentScopes, onChange }: ScopeSelectorProps) {
  const selectedList = currentScopes.split(',').map(s => s.trim()).filter(Boolean);
  
  const handleToggle = (scope: string) => {
    const nextList = selectedList.includes(scope)
      ? selectedList.filter(s => s !== scope)
      : [...selectedList, scope];
    onChange(nextList.join(', '));
  };
  
  return (
    <div className="scope-checkboxes" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', margin: '8px 0', padding: '10px', background: '#1e293b', borderRadius: '6px', border: '1px solid #334155' }}>
      {AVAILABLE_SCOPES.map(scope => {
        const checked = selectedList.includes(scope);
        return (
          <label key={scope} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', cursor: 'pointer', userSelect: 'none', color: checked ? '#38bdf8' : '#94a3b8', transition: 'color 0.2s' }}>
            <input
              type="checkbox"
              checked={checked}
              onChange={() => handleToggle(scope)}
              style={{ cursor: 'pointer', accentColor: '#0284c7' }}
            />
            {scope}
          </label>
        );
      })}
    </div>
  );
}

const apiBaseUrl = () => {
  if (window.location.hostname === 'data.getundiluted.com' || window.location.hostname === 'data.undiluted.com') {
    return `${window.location.protocol}//${window.location.host}/api/v1`;
  }
  const fromStorage = localStorage.getItem('research_api_base_url');
  if (fromStorage) return fromStorage;
  const fromEnv = typeof process !== 'undefined' ? process.env?.RESEARCH_API_BASE_URL : undefined;
  if (fromEnv) return fromEnv;
  if (window.location.pathname.startsWith('/research')) {
    return `${window.location.protocol}//${window.location.host}/research-api/v1`;
  }
  return 'http://localhost:8010/api/v1';
};

interface PaginationProps {
  page: number;
  limit: number;
  total: number;
  onChange: (page: number) => void;
  onLimitChange?: (limit: number) => void;
}

function Pagination({ page, limit, total, onChange, onLimitChange }: PaginationProps) {
  const totalPages = Math.ceil(total / limit) || 1;

  const handlePageInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value, 10);
    if (!isNaN(val) && val >= 1 && val <= totalPages) {
      onChange(val);
    }
  };

  return (
    <div className="pagination" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: '12px', marginTop: '16px', padding: '12px', background: '#1e293b', borderRadius: '8px', border: '1px solid #334155' }}>
      {totalPages > 1 && (
        <>
          <button
            disabled={page <= 1}
            onClick={() => onChange(1)}
            style={{ padding: '6px 12px', fontSize: '0.85rem', cursor: page <= 1 ? 'not-allowed' : 'pointer', background: '#334155', border: 'none', borderRadius: '4px', color: '#f8fafc', opacity: page <= 1 ? 0.5 : 1 }}
          >
            First
          </button>
          <button
            disabled={page <= 1}
            onClick={() => onChange(page - 1)}
            style={{ padding: '6px 12px', fontSize: '0.85rem', cursor: page <= 1 ? 'not-allowed' : 'pointer', background: '#334155', border: 'none', borderRadius: '4px', color: '#f8fafc', opacity: page <= 1 ? 0.5 : 1 }}
          >
            Previous
          </button>
          
          <span style={{ fontSize: '0.85rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>Page</span>
            <input
              type="number"
              min={1}
              max={totalPages}
              value={page}
              onChange={handlePageInput}
              style={{ width: '60px', background: '#0f172a', border: '1px solid #475569', color: '#f8fafc', borderRadius: '4px', padding: '4px 6px', textAlign: 'center', fontSize: '0.85rem' }}
            />
            <span>of <strong style={{ color: '#f8fafc' }}>{totalPages}</strong> (Total: {total})</span>
          </span>

          <button
            disabled={page >= totalPages}
            onClick={() => onChange(page + 1)}
            style={{ padding: '6px 12px', fontSize: '0.85rem', cursor: page >= totalPages ? 'not-allowed' : 'pointer', background: '#334155', border: 'none', borderRadius: '4px', color: '#f8fafc', opacity: page >= totalPages ? 0.5 : 1 }}
          >
            Next
          </button>
          <button
            disabled={page >= totalPages}
            onClick={() => onChange(totalPages)}
            style={{ padding: '6px 12px', fontSize: '0.85rem', cursor: page >= totalPages ? 'not-allowed' : 'pointer', background: '#334155', border: 'none', borderRadius: '4px', color: '#f8fafc', opacity: page >= totalPages ? 0.5 : 1 }}
          >
            Last
          </button>
        </>
      )}
      {totalPages <= 1 && (
        <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Total: {total}</span>
      )}
      {onLimitChange && (
        <span style={{ fontSize: '0.85rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '8px', marginLeft: totalPages > 1 ? '16px' : '0' }}>
          <span>Show:</span>
          <select
            value={limit}
            onChange={(e) => onLimitChange(parseInt(e.target.value, 10))}
            style={{ background: '#0f172a', border: '1px solid #475569', color: '#f8fafc', borderRadius: '4px', padding: '4px 6px', fontSize: '0.85rem', cursor: 'pointer' }}
          >
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={250}>250</option>
            <option value={500}>500</option>
          </select>
        </span>
      )}
    </div>
  );
}

const joinUrl = (base: string, path: string) => `${base.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;

const lakeTimeframeLabel = (timeframe: string, multiplier: number) => {
  const suffix = timeframe === 'day' ? 'd' : timeframe === 'minute' ? 'm' : timeframe === 'hour' ? 'h' : timeframe;
  return `${multiplier}${suffix}`;
};

const parseJwt = (token: string) => {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
};

const readStoredToken = () => {
  const token = localStorage.getItem('token') || '';
  if (!token) return '';
  const payload = parseJwt(token);
  const expiration = Number(payload?.exp);
  if (!Number.isFinite(expiration) || expiration * 1000 <= Date.now()) {
    localStorage.removeItem('token');
    return '';
  }
  return token;
};

const priceStorageLabel = (storage: 'timescale' | 'lake') => (
  storage === 'lake' ? 'Parquet Lake (DuckDB)' : 'Timescale DB'
);

const storageFromLabel = (label: string): 'timescale' | 'lake' => (
  label === 'Parquet Lake (DuckDB)' ? 'lake' : 'timescale'
);

const authHeaders = (apiKey: string) => {
  const headers = new Headers({ 'Content-Type': 'application/json' });
  if (apiKey) headers.set('X-API-Key', apiKey);
  const token = localStorage.getItem('token');
  if (!apiKey && token) headers.set('Authorization', `Bearer ${token}`);
  return headers;
};

const authOnlyHeaders = (apiKey: string) => {
  const headers = new Headers();
  if (apiKey) headers.set('X-API-Key', apiKey);
  const token = localStorage.getItem('token');
  if (!apiKey && token) headers.set('Authorization', `Bearer ${token}`);
  return headers;
};

const formatDate = (value?: string) => {
  if (!value) return '-';
  return new Date(value).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
};

const metric = (value: any) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '-';
  return number.toLocaleString(undefined, { maximumFractionDigits: 3 });
};

const tabHelp: Record<Tab, { title: string; body: string }> = {
  backtests: {
    title: 'Explore saved backtests',
    body: 'Filter by symbol or strategy. Rows are summaries; saved result detail remains available through the research API and bridged trading routes.',
  },
  portfolios: {
    title: 'Explore portfolio simulations',
    body: 'Portfolio rows combine saved strategy allocations, metrics, correlations, chart data, and trades stored in the research lake.',
  },
  optimizations: {
    title: 'Explore optimization runs',
    body: 'Use this view to compare saved parameter sweeps. The combinations column shows how many ranked parameter results are stored.',
  },
  prices: {
    title: 'Explore price and daily volatility history',
    body: 'Price bars and daily IV/HV are stored as separate series so volatility observations cannot replace traded prices.',
  },
  artifacts: {
    title: 'Explore research notes and artifacts',
    body: 'Artifacts can hold notes, JSON metadata, imported datasets, and tags for later MCP or API retrieval.',
  },
  users: {
    title: 'Set up research user access',
    body: 'Create or update lake-specific grants for existing app users. Scopes decide whether a user can read, write, delete, use MCP, or manage security.',
  },
  keys: {
    title: 'Set up API keys',
    body: 'Create scoped keys for scripts and external clients. The full key is shown once; store it immediately. Revoked keys stop working.',
  },
  mcp: {
    title: 'Set up MCP access',
    body: 'MCP clients need read:mcp scope through an API key. The MCP tools are read-mostly and backed only by the research lake.',
  },
  audit: {
    title: 'Review research security events',
    body: 'Audit events show key creation, grant changes, deletes, uploads, and other lake actions with actor and resource context.',
  },
};

function App() {
  const [tab, setTab] = useState<Tab>('backtests');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [selectedSeries, setSelectedSeries] = useState<{ symbol: string; sec_type: string; data_source: string; data_type?: string; timeframe: string; multiplier: number; storage?: string } | null>(null);
  const selectedSeriesIsVolatility = Boolean(selectedSeries
    && selectedSeries.data_source.toUpperCase() === 'IBKR'
    && ['IV', 'HV'].includes((selectedSeries.data_type || '').toUpperCase()));
  const formatSelectedSeriesValue = (value: any) => selectedSeriesIsVolatility
    ? `${metric(Number(value) * 100)}%`
    : metric(value);
  const [baseUrl, setBaseUrl] = useState(apiBaseUrl());
  const [apiKey, setApiKey] = useState(localStorage.getItem('research_api_key') || '');
  const [loginApiKey, setLoginApiKey] = useState('');
  const [token, setToken] = useState(readStoredToken);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [items, setItems] = useState<any[]>([]);
  const [selectedRows, setSelectedRows] = useState<Record<Tab, Set<string>>>(emptyRowSelections);
  const [inventoryItems, setInventoryItems] = useState<any[]>([]);
  const [priceLakePath, setPriceLakePath] = useState('');
  const requestVersionRef = useRef(0);
  const pageRequestActiveRef = useRef(false);
  const filterOptionsLoadedRef = useRef(new Set<string>());
  const [priceSymbols, setPriceSymbols] = useState<string[]>([]);
  const [priceSecTypes, setPriceSecTypes] = useState<string[]>([]);
  const [priceDataSources, setPriceDataSources] = useState<string[]>([]);
  const [priceTimeframes, setPriceTimeframes] = useState<string[]>([]);
  const [priceIndexes, setPriceIndexes] = useState<string[]>([]);

  const uniqueSymbols = useMemo(() => {
    if (priceSymbols.length > 0) return priceSymbols;
    const fromInv = inventoryItems.map(x => x.symbol);
    const fromItems = items.map(x => x.symbol);
    return Array.from(new Set([...fromInv, ...fromItems])).filter(Boolean).sort();
  }, [priceSymbols, inventoryItems, items]);

  const uniqueSecTypes = useMemo(() => {
    if (priceSecTypes.length > 0) return priceSecTypes;
    const fromInv = inventoryItems.map(x => x.sec_type);
    const fromItems = items.map(x => x.sec_type);
    return Array.from(new Set([...fromInv, ...fromItems])).filter(Boolean).sort();
  }, [priceSecTypes, inventoryItems, items]);

  const uniqueDataSources = useMemo(() => {
    if (priceDataSources.length > 0) return priceDataSources;
    const fromInv = inventoryItems.map(x => x.data_source);
    const fromItems = items.map(x => x.data_source);
    return Array.from(new Set([...fromInv, ...fromItems]))
      .filter((source) => Boolean(source) && String(source).toUpperCase() !== 'POLYGON')
      .sort();
  }, [priceDataSources, inventoryItems, items]);

  const uniqueTimeframes = useMemo(() => {
    if (priceTimeframes.length > 0) return priceTimeframes;
    const fromInv = inventoryItems.map(x => x.timeframe);
    const fromItems = items.map(x => x.timeframe);
    return Array.from(new Set([...fromInv, ...fromItems])).filter(Boolean).sort();
  }, [priceTimeframes, inventoryItems, items]);

  const uniqueIndexes = useMemo(() => {
    const set = new Set<string>();
    const addValues = (val: unknown) => {
      if (!val) return;
      if (Array.isArray(val)) {
        val.forEach(addValues);
      } else if (typeof val === 'string') {
        val.split(',').forEach(s => {
          const trimmed = s.trim();
          if (trimmed) set.add(trimmed);
        });
      } else {
        set.add(String(val));
      }
    };

    if (priceIndexes.length > 0) {
      priceIndexes.forEach(addValues);
      return Array.from(set).sort();
    }
    [...inventoryItems, ...items].forEach(x => {
      addValues(x?.indexes);
    });
    return Array.from(set).sort();
  }, [priceIndexes, inventoryItems, items]);
  
  const [backtestSymbols, setBacktestSymbols] = useState<string[]>([]);
  const [backtestStrategies, setBacktestStrategies] = useState<string[]>([]);
  const [optimizationSymbols, setOptimizationSymbols] = useState<string[]>([]);
  const [optimizationStrategies, setOptimizationStrategies] = useState<string[]>([]);
  const [artifactTypes, setArtifactTypes] = useState<string[]>([]);
  const [artifactTags, setArtifactTags] = useState<string[]>([]);
  
  const [audit, setAudit] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState('');
  const [error, setError] = useState('');
  const [authMessage, setAuthMessage] = useState('');
  const [pendingAction, setPendingAction] = useState<{ id: string; label: string } | null>(null);
  const actionLockRef = useRef(false);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventoryLoadedFor, setInventoryLoadedFor] = useState<'timescale' | 'lake' | null>(null);
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);
  const [filterOptionsError, setFilterOptionsError] = useState('');
  const [refreshSequence, setRefreshSequence] = useState(0);
  const [createdKey, setCreatedKey] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(!Boolean(localStorage.getItem('token') || localStorage.getItem('research_api_key')));
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [filters, setFilters] = useState({ symbol: '', strategy: '', tag: '', artifact_type: '', sec_type: '', data_source: '', timeframe: '', indexes: '' });
  const [priceStorage, setPriceStorage] = useState<'timescale' | 'lake'>(() => {
    const saved = localStorage.getItem('research_price_storage');
    return saved === 'timescale' || saved === 'lake' ? saved : 'lake';
  });
  const [keyForm, setKeyForm] = useState({ name: 'research-client', scopes: 'read:artifacts,read:mcp' });
  const [userForm, setUserForm] = useState({ user_id: '', username: '', password: '', role: 'viewer', scopes: 'read:artifacts' });
  const [mcpForm, setMcpForm] = useState({ client_name: 'codex', scopes: 'read:mcp' });
  const [priceUpload, setPriceUpload] = useState({
    mode: 'json',
    symbol: 'QQQ',
    sec_type: 'STK',
    stk_type: '',
    data_source: 'RESEARCH',
    timeframe: 'day',
    multiplier: '1',
    barsJson: '[{"timestamp":"2026-01-02T00:00:00","open":100,"high":101,"low":99,"close":100.5,"volume":1000}]',
  });
  const [priceUploadFiles, setPriceUploadFiles] = useState<File[]>([]);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedItems = useMemo(() => {
    if (!sortConfig) return items;
    return [...items].sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      
      if (sortConfig.key === 'total_return' || sortConfig.key === 'sharpe') {
        valA = a.metrics?.[sortConfig.key];
        valB = b.metrics?.[sortConfig.key];
      }

      if (valA === undefined || valA === null) return 1;
      if (valB === undefined || valB === null) return -1;
      
      if (typeof valA === 'string' && typeof valB === 'string') {
        return sortConfig.direction === 'asc' 
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }
      
      return sortConfig.direction === 'asc'
        ? (valA > valB ? 1 : -1)
        : (valA < valB ? 1 : -1);
    });
  }, [items, sortConfig]);

  const canBulkDelete = BULK_DELETE_TABS.includes(tab);
  const selectedRowIds = selectedRows[tab];
  const pageRowIds = sortedItems
    .map(item => getRowId(tab, item))
    .filter(id => Boolean(id && !(tab === 'keys' && id.startsWith('env-'))));
  const allPageRowsSelected = pageRowIds.length > 0 && pageRowIds.every(id => selectedRowIds.has(id));
  const allFilteredSelected = total > 0 && (selectedRowIds.size >= total || (pageRowIds.length === total && allPageRowsSelected));

  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const headerMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (headerCheckboxRef.current) {
      const isIndeterminate = selectedRowIds.size > 0 && !allFilteredSelected;
      headerCheckboxRef.current.indeterminate = isIndeterminate;
    }
  }, [selectedRowIds, allFilteredSelected]);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (headerMenuRef.current && !headerMenuRef.current.contains(e.target as Node)) {
        setHeaderMenuOpen(false);
      }
    };
    if (headerMenuOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
      return () => document.removeEventListener('mousedown', handleOutsideClick);
    }
  }, [headerMenuOpen]);

  const currentUser = useMemo(() => parseJwt(token), [token]);
  const sessionTitle = currentUser?.exp
    ? `Research session expires ${formatDate(new Date(Number(currentUser.exp) * 1000).toISOString())}`
    : undefined;
  const [activeColumns, setActiveColumns] = useState<Record<Tab, string[]>>({
    backtests: ['id', 'timestamp', 'strategy', 'symbol', 'timeframe', 'total_return', 'sharpe'],
    portfolios: ['id', 'timestamp', 'name', 'initial_capital', 'total_return', 'sharpe'],
    optimizations: ['id', 'timestamp', 'strategy', 'symbol', 'optimize_for', 'combinations'],
    prices: ['symbol', 'sec_type', 'con_id', 'stk_type', 'indexes', 'exchange', 'data_source', 'timeframe', 'multiplier', 'start', 'end', 'bars', 'updated_at'],
    artifacts: ['id', 'updated_at', 'title', 'artifact_type', 'tags'],
    keys: ['id', 'name', 'key_prefix', 'scopes', 'user_id', 'is_active', 'last_used_at'],
    users: ['user_id', 'username', 'role', 'scopes', 'is_active'],
    mcp: ['id', 'client_name', 'key_prefix', 'scopes', 'is_active', 'last_used_at'],
    audit: ['created_at', 'actor_type', 'actor_id', 'action', 'resource_type', 'resource_id', 'detail'],
  });
  const isAuthenticated = Boolean(token || apiKey);

  useEffect(() => {
    setSelectedRows(emptyRowSelections());
  }, [token, apiKey]);

  useEffect(() => {
    localStorage.setItem('research_api_base_url', baseUrl);
  }, [baseUrl]);

  useEffect(() => {
    if (token) localStorage.setItem('token', token);
    else localStorage.removeItem('token');
  }, [token]);

  useEffect(() => {
    if (!token) return;
    const expiration = Number(currentUser?.exp);
    if (!Number.isFinite(expiration)) return;
    const expiresIn = expiration * 1000 - Date.now();
    const expireSession = () => {
      localStorage.removeItem('token');
      setToken('');
      setLoginModalOpen(true);
      setAuthMessage('Your research login has expired. Sign in again to continue.');
    };
    if (expiresIn <= 0) {
      expireSession();
      return;
    }
    const timeout = window.setTimeout(expireSession, expiresIn);
    return () => window.clearTimeout(timeout);
  }, [token, currentUser]);

  useEffect(() => {
    if (apiKey) localStorage.setItem('research_api_key', apiKey);
    else localStorage.removeItem('research_api_key');
  }, [apiKey]);

  useEffect(() => {
    localStorage.setItem('research_price_storage', priceStorage);
    setInventoryItems([]);
    setInventoryLoadedFor(null);
    if (tab === 'prices') {
      syncStateToUrl('prices', 1, limit, filters, null);
    }
  }, [priceStorage]);

  const syncStateToUrl = (newTab: Tab, newPage: number, newLimit: number, newFilters: typeof filters, newSelectedSeries: typeof selectedSeries) => {
    const newUrl = buildResearchRouteUrl(window.location.pathname, newTab, newPage, newLimit, newFilters, newSelectedSeries);
    const currentUrl = `${window.location.pathname}${window.location.search}`;
    if (currentUrl !== newUrl) {
      window.history.pushState(null, '', newUrl);
    }

    setTab(newTab);
    setPage(newPage);
    setLimit(newLimit);
    setFilters(newFilters);
    setSelectedSeries(newSelectedSeries);
  };

  useEffect(() => {
    const handleLocationChange = () => {
      const route = parseResearchRoute(window.location.pathname, window.location.search, window.location.hash);
      if (route.needsReplace) window.history.replaceState(null, '', route.url);

      const currentTab = route.tab;
      const p = parseInt(route.params.get('page') || '1', 10) || 1;
      const l = parseInt(route.params.get('limit') || '20', 10) || 20;
      
      setTab(prevTab => {
        if (prevTab !== currentTab) {
          setItems([]);
          setTotal(0);
        }
        return currentTab;
      });
      setPage(p);
      setLimit(l);
      setFilters(prev => {
        const next = {
          symbol: route.params.get('symbol') || '',
          strategy: route.params.get('strategy') || '',
          tag: route.params.get('tag') || '',
          artifact_type: route.params.get('artifact_type') || '',
          sec_type: route.params.get('sec_type') || '',
          data_source: route.params.get('data_source') || '',
          timeframe: route.params.get('timeframe') || '',
          indexes: route.params.get('indexes') || '',
        };
        const unchanged = Object.keys(next).every(k => (next as any)[k] === (prev as any)[k]);
        return unchanged ? prev : next;
      });

      if (currentTab === 'prices' && route.params.get('s_symbol')) {
        setSelectedSeries({
          symbol: route.params.get('s_symbol') || '',
          sec_type: route.params.get('s_sec_type') || 'STK',
          data_source: route.params.get('s_data_source') || 'RESEARCH',
          data_type: route.params.get('s_data_type') || undefined,
          timeframe: route.params.get('s_timeframe') || 'day',
          multiplier: parseInt(route.params.get('s_multiplier') || '1', 10) || 1,
          storage: route.params.get('s_storage') || undefined,
        });
      } else {
        setSelectedSeries(null);
      }
    };

    window.addEventListener('popstate', handleLocationChange);
    handleLocationChange();
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);

  const handleTabChange = (newTab: Tab) => {
    const emptyFilters = { symbol: '', strategy: '', tag: '', artifact_type: '', sec_type: '', data_source: '', timeframe: '', indexes: '' };
    setTab(newTab);
    setPage(1);
    setFilters(emptyFilters);
    setSelectedSeries(null);
    setItems([]);
    setTotal(0);
    syncStateToUrl(newTab, 1, limit, emptyFilters, null);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    syncStateToUrl(tab, newPage, limit, filters, tab === 'prices' ? selectedSeries : null);
  };

  const handleFilterChange = (field: string, value: string) => {
    const nextFilters = { ...filters, [field]: value };
    setFilters(nextFilters);
    setPage(1);
    syncStateToUrl(tab, 1, limit, nextFilters, tab === 'prices' ? selectedSeries : null);
  };

  const handleSelectSeries = (item: any) => {
    setPage(1);
    setItems([]);
    setTotal(0);
    const seriesObj = { ...item, storage: item.storage || priceStorage };
    setSelectedSeries(seriesObj);
    syncStateToUrl('prices', 1, limit, filters, seriesObj);
  };

  const handleCloseSeries = () => {
    setSelectedSeries(null);
    setPage(1);
    setItems([]);
    setTotal(0);
    syncStateToUrl('prices', 1, limit, filters, null);
  };

  const handleUnauthorized = useCallback(() => {
    const hadStoredCredentials = Boolean(localStorage.getItem('token') || localStorage.getItem('research_api_key'));
    if (!hadStoredCredentials) return;
    localStorage.removeItem('token');
    localStorage.removeItem('research_api_key');
    setToken('');
    setApiKey('');
    setLoginModalOpen(true);
    setAuthMessage('The saved research credentials are no longer valid. Sign in again or enter a valid API key.');
  }, []);

  const request = useCallback(async (path: string, options: RequestInit = {}) => {
    const res = await fetch(joinUrl(baseUrl, path), {
      ...options,
      headers: authHeaders(apiKey),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      if (res.status === 401) handleUnauthorized();
      throw new Error(body.detail || `${res.status} ${res.statusText}`);
    }
    return res.json();
  }, [apiKey, baseUrl, handleUnauthorized]);

  const runAction = async (id: string, label: string, action: () => Promise<void>) => {
    if (actionLockRef.current) return;
    actionLockRef.current = true;
    setError('');
    setPendingAction({ id, label });
    try {
      await action();
    } catch (err) {
      setError(err instanceof Error ? err.message : `${label} failed`);
    } finally {
      actionLockRef.current = false;
      setPendingAction(null);
    }
  };

  const load = useCallback(async () => {
    if (!isAuthenticated) {
      requestVersionRef.current += 1;
      pageRequestActiveRef.current = false;
      filterOptionsLoadedRef.current.clear();
      setLoading(false);
      setItems([]);
      setAudit([]);
      setInventoryItems([]);
      setInventoryLoadedFor(null);
      setBacktestSymbols([]);
      setBacktestStrategies([]);
      setOptimizationSymbols([]);
      setOptimizationStrategies([]);
      setArtifactTypes([]);
      setArtifactTags([]);
      setPriceSymbols([]);
      setPriceSecTypes([]);
      setPriceDataSources([]);
      setPriceTimeframes([]);
      setPriceIndexes([]);
      setFilterOptionsLoading(false);
      setFilterOptionsError('');
      setPriceLakePath('');
      setTotal(0);
      setError('');
      setLoadingStatus('');
      return;
    }
    pageRequestActiveRef.current = true;
    setLoading(true);
    setLoadingStatus('Preparing request...');
    setError('');
    const version = ++requestVersionRef.current;
    const setPhase = (message: string) => {
      if (version === requestVersionRef.current) setLoadingStatus(message);
    };
    try {
      const query = new URLSearchParams();
      if (filters.symbol && ['backtests', 'optimizations', 'prices'].includes(tab)) query.set('symbol', filters.symbol.toUpperCase());
      if (filters.strategy && ['backtests', 'optimizations'].includes(tab)) query.set('strategy', filters.strategy);
      if (tab === 'prices' && !selectedSeries) {
        if (filters.sec_type) query.set('sec_type', filters.sec_type.toUpperCase());
        if (filters.data_source) query.set('data_source', filters.data_source.toUpperCase());
        if (filters.timeframe) query.set('timeframe', filters.timeframe);
        if (filters.indexes) query.set('indexes', filters.indexes.toUpperCase());
      }
      if (filters.tag && tab === 'artifacts') query.set('tag', filters.tag);
      if (filters.artifact_type && tab === 'artifacts') query.set('artifact_type', filters.artifact_type);

      if (tab === 'audit') {
        setPhase('Loading latest audit events from admin Postgres...');
        const data = await request('/security/audit?limit=200');
        if (version !== requestVersionRef.current) return;
        setPhase(`Rendering ${data.items?.length || 0} audit events...`);
        setAudit(data.items || []);
        setItems([]);
        setTotal(data.items?.length || 0);
      } else if (tab === 'keys') {
        setPhase('Loading API keys from admin Postgres...');
        const data = await request('/security/api-keys');
        if (version !== requestVersionRef.current) return;
        setPhase(`Rendering ${data.items?.length || 0} API keys...`);
        setItems(data.items || []);
        setTotal(data.items?.length || 0);
      } else if (tab === 'users') {
        setPhase('Loading research users from admin Postgres...');
        const data = await request('/security/users');
        if (version !== requestVersionRef.current) return;
        setPhase(`Rendering ${data.items?.length || 0} users...`);
        setItems(data.items || []);
        setTotal(data.items?.length || 0);
      } else if (tab === 'mcp') {
        setPhase('Loading MCP grants from admin Postgres...');
        const data = await request('/security/mcp-grants');
        if (version !== requestVersionRef.current) return;
        setPhase(`Rendering ${data.items?.length || 0} MCP grants...`);
        setItems(data.items || []);
        setTotal(data.items?.length || 0);
      } else if (tab === 'prices') {
        if (selectedSeries) {
          const seriesQuery = new URLSearchParams();
          seriesQuery.set('symbol', selectedSeries.symbol);
          seriesQuery.set('sec_type', selectedSeries.sec_type);
          seriesQuery.set('data_source', selectedSeries.data_source);
          if (selectedSeries.data_type) {
            seriesQuery.set('data_type', selectedSeries.data_type);
          }
          seriesQuery.set('timeframe', selectedSeries.timeframe);
          seriesQuery.set('multiplier', String(selectedSeries.multiplier));
          seriesQuery.set('limit', String(limit));
          seriesQuery.set('offset', String((page - 1) * limit));
          const isDailyVolatility = selectedSeries.data_source.toUpperCase() === 'IBKR'
            && ['IV', 'HV'].includes((selectedSeries.data_type || '').toUpperCase());
          const storage = isDailyVolatility ? 'timescale' : selectedSeries.storage === 'lake' ? 'lake' : priceStorage;
          setPhase(`Loading ${selectedSeries.symbol} ${selectedSeries.timeframe} ${isDailyVolatility ? 'volatility observations' : 'bars'} from ${storage === 'lake' ? 'Parquet lake' : 'Timescale'}...`);
          const data = isDailyVolatility
            ? await request(`/volatility/daily/series?${new URLSearchParams({
                symbol: selectedSeries.symbol,
                sec_type: selectedSeries.sec_type,
                data_source: selectedSeries.data_source,
                volatility_type: selectedSeries.data_type || 'IV',
                limit: String(limit),
                offset: String((page - 1) * limit),
              }).toString()}`)
            : await request(`/prices/${storage === 'lake' ? 'lake/' : ''}series?${seriesQuery.toString()}`);
          if (version !== requestVersionRef.current) return;
          setPhase(`Rendering ${data.data?.bars?.length || 0} bars (${data.data?.total || 0} total)...`);
          setItems(data.data?.bars || []);
          setTotal(data.data?.total || 0);
          if (data.lake_path) setPriceLakePath(data.lake_path);
        } else {
          query.set('skip', String((page - 1) * limit));
          query.set('limit', String(limit));
          let activeStorage = priceStorage;
          setPhase(`Loading market-data inventory from ${activeStorage === 'lake' ? 'Parquet lake' : 'Timescale'}...`);
          let data = await request(`/prices/${activeStorage === 'lake' ? 'lake/' : ''}inventory?${query.toString()}`);
          if (activeStorage === 'timescale' && (data.total || 0) === 0) {
            setPhase('Timescale inventory is empty; checking Parquet lake fallback...');
            const lakeData = await request(`/prices/lake/inventory?${query.toString()}`);
            if ((lakeData.total || 0) > 0) {
              activeStorage = 'lake';
              data = lakeData;
              setPriceStorage('lake');
            }
          }
          if (version !== requestVersionRef.current) return;
          const mergedPriceItems = mergePriceItems(data.items || []);
          setPhase(`Rendering ${mergedPriceItems.length} market-data series (${data.total || 0} total)...`);
          setItems(mergedPriceItems);
          setTotal(data.total || 0);
          setPriceLakePath(data.lake_path || '');
        }
      } else if (tab === 'backtests') {
        query.set('skip', String((page - 1) * limit));
        query.set('limit', String(limit));
        setPhase('Loading paged backtest results...');
        const data = await request(`/backtests?${query.toString()}`);
        if (version !== requestVersionRef.current) return;
        setPhase(`Rendering ${data.items?.length || 0} backtests (${data.total || 0} total)...`);
        setItems(data.items || []);
        setTotal(data.total || 0);
      } else if (tab === 'optimizations') {
        query.set('skip', String((page - 1) * limit));
        query.set('limit', String(limit));
        setPhase('Loading paged optimization results...');
        const data = await request(`/optimizations?${query.toString()}`);
        if (version !== requestVersionRef.current) return;
        setPhase(`Rendering ${data.items?.length || 0} optimizations (${data.total || 0} total)...`);
        setItems(data.items || []);
        setTotal(data.total || 0);
      } else if (tab === 'artifacts') {
        query.set('skip', String((page - 1) * limit));
        query.set('limit', String(limit));
        query.set('summary', 'true');
        setPhase('Loading paged artifacts...');
        const data = await request(`/artifacts?${query.toString()}`);
        if (version !== requestVersionRef.current) return;
        setPhase(`Rendering ${data.items?.length || 0} artifacts (${data.total || 0} total)...`);
        setItems(data.items || []);
        setTotal(data.total || 0);
      } else {
        const path = '/portfolios';
        query.set('skip', String((page - 1) * limit));
        query.set('limit', String(limit));
        query.set('summary', 'true');
        setPhase('Loading portfolio results...');
        const data = await request(`${path}?${query.toString()}`);
        if (version !== requestVersionRef.current) return;
        setPhase(`Rendering ${data.items?.length || 0} portfolios (${data.total || 0} total)...`);
        setItems(data.items || []);
        setTotal(data.total || 0);
      }
    } catch (err: any) {
      if (version === requestVersionRef.current) {
        setError(err.message || 'Failed to load research lake');
      }
    } finally {
      if (version === requestVersionRef.current) {
        pageRequestActiveRef.current = false;
        setLoading(false);
      }
    }
  }, [filters, isAuthenticated, request, tab, page, limit, selectedSeries, priceStorage]);

  useEffect(() => {
    load();
  }, [load, refreshSequence]);

  useEffect(() => {
    if (!isAuthenticated || !['backtests', 'optimizations', 'artifacts', 'prices'].includes(tab)) {
      setFilterOptionsLoading(false);
      setFilterOptionsError('');
      if (!isAuthenticated) filterOptionsLoadedRef.current.clear();
      return;
    }

    const credentialKey = token
      ? `jwt:${parseJwt(token)?.id ?? token.slice(-8)}`
      : `key:${apiKey.slice(-8)}`;
    const cacheKey = `${baseUrl}:${credentialKey}:${tab}:${tab === 'prices' ? priceStorage : ''}:${refreshSequence}`;
    if (filterOptionsLoadedRef.current.has(cacheKey)) return;

    let cancelled = false;
    const endpoint = tab === 'backtests'
      ? '/filters/backtests'
      : tab === 'optimizations'
        ? '/filters/optimizations'
        : tab === 'artifacts'
          ? '/filters/artifacts'
          : `/filters/prices?storage=${priceStorage}`;
    setFilterOptionsLoading(true);
    setFilterOptionsError('');
    request(endpoint)
      .then(data => {
        if (cancelled) return;
        filterOptionsLoadedRef.current.add(cacheKey);
        if (tab === 'backtests') {
          setBacktestSymbols(data.symbols || []);
          setBacktestStrategies(data.strategies || []);
        } else if (tab === 'optimizations') {
          setOptimizationSymbols(data.symbols || []);
          setOptimizationStrategies(data.strategies || []);
        } else if (tab === 'artifacts') {
          setArtifactTypes(data.artifact_types || []);
          setArtifactTags(data.tags || []);
        } else if (tab === 'prices') {
          setPriceSymbols(data.symbols || []);
          setPriceSecTypes(data.sec_types || []);
          setPriceDataSources(data.data_sources || []);
          setPriceTimeframes(data.timeframes || []);
          setPriceIndexes(data.indexes || []);
        }
      })
      .catch(err => {
        if (!cancelled) setFilterOptionsError(err.message || 'Could not load filter options');
      })
      .finally(() => {
        if (!cancelled) setFilterOptionsLoading(false);
      });
    return () => { cancelled = true; };
  }, [tab, isAuthenticated, request, token, apiKey, baseUrl, refreshSequence, priceStorage]);

  useEffect(() => {
    if (tab !== 'prices' || !isAuthenticated || inventoryLoadedFor === priceStorage) return;
    let cancelled = false;
    setInventoryLoading(true);
    request(`/prices/${priceStorage === 'lake' ? 'lake/' : ''}inventory`)
      .then(data => {
        if (cancelled) return;
        setInventoryItems(mergePriceItems(data.items || []));
        setPriceLakePath(data.lake_path || '');
        setInventoryLoadedFor(priceStorage);
      })
      .catch(err => {
        if (!cancelled) setError(`Could not load price filter options: ${err.message || 'Request failed'}`);
      })
      .finally(() => {
        if (!cancelled) setInventoryLoading(false);
      });
    return () => { cancelled = true; };
  }, [tab, isAuthenticated, request, priceStorage, inventoryLoadedFor]);

  const login = async () => {
    setError('');
    setAuthMessage('');
    if (!loginForm.username.trim() || !loginForm.password) {
      setError('Enter a username and password');
      return;
    }
    setLoginLoading(true);
    const body = new URLSearchParams();
    body.set('username', loginForm.username);
    body.set('password', loginForm.password);
    try {
      const res = await fetch(joinUrl(baseUrl, '/auth/token'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || 'Login failed');
        return;
      }
      const data = await res.json();
      localStorage.setItem('token', data.access_token);
      setToken(data.access_token);
      setApiKey('');
      setLoginForm({ username: '', password: '' });
      setAuthMessage('');
      setLoginModalOpen(false);
    } catch {
      setError(`Login API is unreachable at ${joinUrl(baseUrl, '/auth/token')}. Start the research API and confirm CORS allows this explorer origin.`);
    } finally {
      setLoginLoading(false);
    }
  };

  const logout = async () => {
    localStorage.removeItem('token');
    localStorage.removeItem('research_api_key');
    setToken('');
    setApiKey('');
    setLoginApiKey('');
    setItems([]);
    setAudit([]);
    setLoginModalOpen(true);
  };

  const activateApiKey = async () => {
    const nextKey = loginApiKey.trim();
    if (!nextKey) {
      throw new Error('Enter an API key');
    }
    const res = await fetch(joinUrl(baseUrl, '/auth/me'), { headers: authHeaders(nextKey) });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.detail || `${res.status} ${res.statusText}`);
    }
    setApiKey(nextKey);
    setToken('');
    setLoginApiKey('');
    setError('');
    setAuthMessage('');
    setLoginModalOpen(false);
  };

  const createKey = async () => {
    setCreatedKey('');
    const data = await request('/security/api-keys', {
      method: 'POST',
      body: JSON.stringify({ name: keyForm.name, scopes: keyForm.scopes.split(',').map((s) => s.trim()).filter(Boolean) }),
    });
    setCreatedKey(data.api_key);
    await load();
  };

  const revokeKey = async (id: number) => {
    await request(`/security/api-keys/${id}`, { method: 'DELETE' });
    await load();
  };

  const deleteItem = async (type: Tab, id: number) => {
    if (!window.confirm(`Are you sure you want to delete this ${type.slice(0, -1)}?`)) return;
    await request(`/${type}/${id}`, { method: 'DELETE' });
    setSelectedRows(current => {
      const next = new Set(current[type]);
      next.delete(String(id));
      return { ...current, [type]: next };
    });
    await load();
  };

  const updateSelectedRows = (type: Tab, ids: string[], selected: boolean) => {
    setSelectedRows(current => {
      const next = new Set(current[type]);
      ids.forEach(id => selected ? next.add(id) : next.delete(id));
      return { ...current, [type]: next };
    });
  };

  const fetchAllFilteredIds = useCallback(async (targetTab: Tab): Promise<string[]> => {
    if (targetTab === 'keys') {
      const data = await request('/security/api-keys');
      const keyItems = data.items || [];
      return keyItems
        .map((item: any) => getRowId('keys', item))
        .filter((id: string) => Boolean(id && !id.startsWith('env-')));
    }

    if (targetTab === 'prices') {
      if (selectedSeries) return [];
      const query = new URLSearchParams();
      if (filters.symbol) query.set('symbol', filters.symbol.toUpperCase());
      if (filters.sec_type) query.set('sec_type', filters.sec_type.toUpperCase());
      if (filters.data_source) query.set('data_source', filters.data_source.toUpperCase());
      if (filters.timeframe) query.set('timeframe', filters.timeframe);
      if (filters.indexes) query.set('indexes', filters.indexes.toUpperCase());
      const data = await request(`/prices/${priceStorage === 'lake' ? 'lake/' : ''}inventory?${query.toString()}`);
      const priceItems = mergePriceItems(data.items || []);
      const ids = priceItems.map((item: any) => getRowId('prices', item)).filter(Boolean);
      return Array.from(new Set(ids));
    }

    if (['backtests', 'portfolios', 'optimizations', 'artifacts'].includes(targetTab)) {
      const query = new URLSearchParams();
      if (filters.symbol && ['backtests', 'optimizations'].includes(targetTab)) {
        query.set('symbol', filters.symbol.toUpperCase());
      }
      if (filters.strategy && ['backtests', 'optimizations'].includes(targetTab)) {
        query.set('strategy', filters.strategy);
      }
      if (filters.tag && targetTab === 'artifacts') {
        query.set('tag', filters.tag);
      }
      if (filters.artifact_type && targetTab === 'artifacts') {
        query.set('artifact_type', filters.artifact_type);
      }
      if (targetTab === 'artifacts' || targetTab === 'portfolios') {
        query.set('summary', 'true');
      }

      const pageSize = 10000;
      query.set('skip', '0');
      query.set('limit', String(pageSize));
      const firstPage = await request(`/${targetTab}?${query.toString()}`);
      const totalCount = firstPage.total || 0;
      let allItems: any[] = firstPage.items || [];

      if (totalCount > pageSize) {
        const remainingPages: Promise<any>[] = [];
        for (let skip = pageSize; skip < totalCount; skip += pageSize) {
          const nextQuery = new URLSearchParams(query);
          nextQuery.set('skip', String(skip));
          nextQuery.set('limit', String(pageSize));
          remainingPages.push(request(`/${targetTab}?${nextQuery.toString()}`));
        }
        const pageResults = await Promise.all(remainingPages);
        for (const res of pageResults) {
          if (res.items) {
            allItems = allItems.concat(res.items);
          }
        }
      }

      return allItems.map((item: any) => getRowId(targetTab, item)).filter(Boolean);
    }

    return [];
  }, [filters, priceStorage, request, selectedSeries]);

  const selectAllFiltered = async () => {
    if (selectAllLoading || !canBulkDelete) return;
    setSelectAllLoading(true);
    try {
      const allIds = await fetchAllFilteredIds(tab);
      if (allIds.length > 0) {
        setSelectedRows(current => ({
          ...current,
          [tab]: new Set(allIds),
        }));
      }
    } catch (err: any) {
      setError(err instanceof Error ? err.message : 'Failed to select all filtered items');
    } finally {
      setSelectAllLoading(false);
    }
  };

  const handleHeaderCheckboxToggle = () => {
    if (allFilteredSelected) {
      updateSelectedRows(tab, Array.from(selectedRowIds), false);
    } else if (allPageRowsSelected) {
      if (total > pageRowIds.length) {
        void selectAllFiltered();
      } else {
        updateSelectedRows(tab, pageRowIds, false);
      }
    } else {
      updateSelectedRows(tab, pageRowIds, true);
    }
  };

  const deleteSelectedRows = async (type: Tab) => {
    const ids = Array.from(selectedRows[type]);
    if (ids.length === 0) return;
    const label = type === 'prices' ? 'price series' : type;
    if (!window.confirm(`Delete ${ids.length} selected ${label}? This cannot be undone.`)) return;

    const results: Array<{ id: string; error?: string }> = [];
    for (let index = 0; index < ids.length; index += 25) {
      if (ids.length > 25) {
        setPendingAction({
          id: `bulk-delete-${type}`,
          label: `Deleting ${label} (${Math.min(index + 25, ids.length)}/${ids.length})…`,
        });
      }
      const batch = ids.slice(index, index + 25);
      const batchResults = await Promise.all(batch.map(async id => {
        try {
          if (type === 'prices') {
            const [symbol, sec_type, data_source, timeframe, multiplier, data_type] = id.split(':');
            const params = new URLSearchParams({
              symbol,
              sec_type,
              data_source,
            });
            const isDailyVolatility = data_source.toUpperCase() === 'IBKR'
              && ['IV', 'HV'].includes((data_type || '').toUpperCase())
              && priceStorage === 'timescale';
            if (isDailyVolatility) {
              params.set('volatility_type', data_type.toUpperCase());
            } else {
              params.set('data_type', data_type || 'TRADES');
              params.set('timeframe', timeframe);
              params.set('multiplier', String(multiplier ?? 1));
            }
            const url = isDailyVolatility
              ? `/volatility/daily/series?${params.toString()}`
              : `/prices/${priceStorage === 'lake' ? 'lake/' : ''}series?${params.toString()}`;
            await request(url, { method: 'DELETE' });
          } else if (type === 'keys') {
            await request(`/security/api-keys/${id}`, { method: 'DELETE' });
          } else {
            await request(`/${type}/${id}`, { method: 'DELETE' });
          }
          return { id };
        } catch (err) {
          return { id, error: err instanceof Error ? err.message : 'Delete failed' };
        }
      }));
      results.push(...batchResults);
    }

    const deletedIds = results.filter(result => !result.error).map(result => result.id);
    const failedResults = results.filter(result => result.error);
    if (deletedIds.length > 0) {
      updateSelectedRows(type, deletedIds, false);
      if (type === 'prices') {
        setInventoryLoadedFor(null);
      }
      await load();
    }
    if (failedResults.length > 0) {
      throw new Error(`${deletedIds.length} deleted; ${failedResults.length} failed. ${failedResults[0].error}`);
    }
  };

  const deletePriceSeries = async (item: any) => {
    const isDailyVolatility = item.data_source?.toUpperCase() === 'IBKR'
      && ['IV', 'HV'].includes((item.data_type || '').toUpperCase())
      && priceStorage === 'timescale';
    const storageLabel = isDailyVolatility ? 'Timescale DB' : priceStorage === 'lake' ? 'Parquet Lake' : 'Timescale DB';
    if (!window.confirm(`Are you sure you want to delete the price series for ${item.symbol} (${item.timeframe}-${item.multiplier}) from ${storageLabel}?`)) return;
    
    const params = new URLSearchParams({
      symbol: item.symbol,
      sec_type: item.sec_type,
      data_source: item.data_source,
    });
    if (isDailyVolatility) {
      params.set('volatility_type', item.data_type.toUpperCase());
    } else {
      params.set('data_type', item.data_type || 'TRADES');
      params.set('timeframe', item.timeframe);
      params.set('multiplier', String(item.multiplier));
    }
    const url = isDailyVolatility
      ? `/volatility/daily/series?${params.toString()}`
      : `/prices/${priceStorage === 'lake' ? 'lake/' : ''}series?${params.toString()}`;
    await request(url, { method: 'DELETE' });
    await load();
  };


  const saveUser = async () => {
    await request('/security/users', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userForm.user_id ? Number(userForm.user_id) : undefined,
        username: userForm.username,
        password: userForm.password || undefined,
        role: userForm.role,
        scopes: userForm.scopes.split(',').map((s) => s.trim()).filter(Boolean),
        is_active: true,
      }),
    });
    await load();
  };

  const createMcpGrant = async () => {
    await request('/security/mcp-grants', {
      method: 'POST',
      body: JSON.stringify({
        client_name: mcpForm.client_name,
        scopes: mcpForm.scopes.split(',').map((s) => s.trim()).filter(Boolean),
        is_active: true,
      }),
    });
    await load();
  };

  const uploadPrices = async () => {
    if (priceUpload.mode === 'parquet') {
      if (!priceUploadFiles.length) {
        throw new Error('Choose at least one Parquet file');
      }
      const form = new FormData();
      priceUploadFiles.forEach((file) => form.append('files', file));
      form.set('symbol', priceUpload.symbol);
      form.set('sec_type', priceUpload.sec_type);
      if (priceUpload.stk_type) form.set('stk_type', priceUpload.stk_type);
      form.set('data_source', priceUpload.data_source);
      form.set('timeframe', priceUpload.timeframe);
      form.set('multiplier', String(Number(priceUpload.multiplier) || 1));
      const res = await fetch(joinUrl(baseUrl, '/prices/lake/parquet/bulk'), {
        method: 'POST',
        headers: authOnlyHeaders(apiKey),
        body: form,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (res.status === 401) handleUnauthorized();
        throw new Error(body.detail || `${res.status} ${res.statusText}`);
      }
      setPriceStorage('lake');
      localStorage.setItem('research_price_storage', 'lake');
      const uploadMultiplier = Number(priceUpload.multiplier) || 1;
      setTab('prices');
      setFilters({ ...filters, symbol: priceUpload.symbol, sec_type: priceUpload.sec_type, data_source: priceUpload.data_source, timeframe: lakeTimeframeLabel(priceUpload.timeframe, uploadMultiplier) });
      setInventoryItems([]);
      setRefreshSequence((sequence) => sequence + 1);
      return;
    }

    let bars: any[];
    try {
      bars = JSON.parse(priceUpload.barsJson);
      if (!Array.isArray(bars)) throw new Error('Bars JSON must be an array');
    } catch (err: any) {
      throw new Error(err.message || 'Invalid bars JSON');
    }
    await request('/prices/bars', {
      method: 'POST',
      body: JSON.stringify({
        symbol: priceUpload.symbol,
        sec_type: priceUpload.sec_type,
        stk_type: priceUpload.stk_type || undefined,
        data_source: priceUpload.data_source,
        timeframe: priceUpload.timeframe,
        multiplier: Number(priceUpload.multiplier) || 1,
        bars,
      }),
    });
    setTab('prices');
    setFilters({ ...filters, symbol: priceUpload.symbol, sec_type: priceUpload.sec_type, data_source: priceUpload.data_source, timeframe: priceUpload.timeframe });
    setInventoryItems([]);
    setRefreshSequence((sequence) => sequence + 1);
  };

  const visibleColumns = useMemo(() => {
    return activeColumns[tab] || [];
  }, [activeColumns, tab]);

  const renderCell = (item: any, column: string) => {
    if (column === 'total_return') return metric(item.metrics?.total_return);
    if (column === 'sharpe') return metric(item.metrics?.sharpe);
    if (column.endsWith('_at') || column === 'timestamp' || column.endsWith('_date')) return formatDate(item[column]);
    if (Array.isArray(item[column])) return item[column].join(', ');
    if (typeof item[column] === 'boolean') return item[column] ? 'Yes' : 'No';
    return item[column] ?? '-';
  };

  const loginModal = loginModalOpen && (
    <div className="modal-backdrop" role="presentation">
      <section className="login-panel modal-panel" role="dialog" aria-modal="true" aria-labelledby="login-title" aria-busy={loginLoading || pendingAction?.id === 'activate-api-key'}>
        <button className="modal-close" onClick={() => setLoginModalOpen(false)} aria-label="Close login" disabled={loginLoading || Boolean(pendingAction)}>
          <X size={16} />
        </button>
        <div className="brand login-brand">
          <Lock className="brand-icon" />
          <div>
            <h1 id="login-title">Research Lake</h1>
            <span>Secure artifact and data explorer</span>
          </div>
        </div>

        <div className="login-grid">
          <form className="login-section" onSubmit={(event) => { event.preventDefault(); void login(); }}>
            <div className="section-title"><LogIn size={16} /> User Login</div>
            <input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} aria-label="Research API URL" disabled={loginLoading || Boolean(pendingAction)} />
            <input value={loginForm.username} autoComplete="username" onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })} placeholder="Username" required disabled={loginLoading || Boolean(pendingAction)} />
            <input value={loginForm.password} autoComplete="current-password" onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} placeholder="Password" type="password" required disabled={loginLoading || Boolean(pendingAction)} />
            <button type="submit" disabled={loginLoading || Boolean(pendingAction)}>
              {loginLoading ? <Loader2 size={15} className="spinner" /> : <LogIn size={15} />}
              {loginLoading ? 'Connecting…' : 'Login'}
            </button>
          </form>

          <form className="login-section" onSubmit={(event) => { event.preventDefault(); void runAction('activate-api-key', 'Checking API key…', activateApiKey); }}>
            <div className="section-title"><KeyRound size={16} /> API Key Access</div>
            <input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} aria-label="Research API URL" disabled={loginLoading || Boolean(pendingAction)} />
            <input value={loginApiKey} onChange={(e) => setLoginApiKey(e.target.value)} placeholder="Research API key" disabled={loginLoading || Boolean(pendingAction)} />
            <button type="submit" disabled={loginLoading || Boolean(pendingAction)}>
              {pendingAction?.id === 'activate-api-key' ? <Loader2 size={15} className="spinner" /> : <KeyRound size={15} />}
              {pendingAction?.id === 'activate-api-key' ? 'Checking key…' : 'Use API Key'}
            </button>
          </form>
        </div>

        <div className="help-panel login-help">
          <Info size={16} />
          <div>
            <strong>Access setup</strong>
            <p>Use research credentials for browser access, or a scoped research API key for scripts and external clients. Admins can create users and API keys after signing in.</p>
          </div>
        </div>

        {authMessage && <div className="error modal-error" role="alert">{authMessage}</div>}
        {error && !authMessage && <div className="error modal-error" role="alert">{error}</div>}
      </section>
    </div>
  );

  const uploadModal = uploadModalOpen && (
    <div className="modal-backdrop" role="presentation">
      <section className="login-panel modal-panel" style={{ width: 'min(640px, 100%)' }} role="dialog" aria-modal="true" aria-labelledby="upload-title">
        <button className="modal-close" onClick={() => setUploadModalOpen(false)} aria-label="Close upload">
          <X size={16} />
        </button>
        <div className="brand login-brand">
          <Upload className="brand-icon" />
          <div>
            <h1 id="upload-title" style={{ fontSize: '1.2rem', margin: 0, color: '#edf5f7' }}>Upload Price Data</h1>
            <span style={{ fontSize: '0.85rem', color: '#8f9aa0' }}>Load JSON bars or Parquet files into research lake</span>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'inline-flex', gap: '6px', padding: '4px', border: '1px solid #384148', borderRadius: '6px', alignSelf: 'flex-start', background: '#171a1d' }}>
            {(['json', 'parquet'] as const).map((mode) => (
              <button
                key={mode}
                onClick={() => setPriceUpload({ ...priceUpload, mode })}
                style={{
                  border: 'none',
                  borderRadius: '4px',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  background: priceUpload.mode === mode ? '#2d7d6b' : 'transparent',
                  color: priceUpload.mode === mode ? '#ffffff' : '#b9c6cc',
                }}
              >
                {mode === 'json' ? 'Timescale DB (JSON)' : 'Parquet Lake (DuckDB)'}
              </button>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '8px' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.8rem', color: '#8f9aa0' }}>
              Symbol
              <input value={priceUpload.symbol} onChange={(e) => setPriceUpload({ ...priceUpload, symbol: e.target.value })} placeholder="Symbol" style={{ background: '#171a1d', border: '1px solid #384148', color: '#edf5f7', padding: '6px 8px', borderRadius: '4px', font: 'inherit' }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.8rem', color: '#8f9aa0' }}>
              Sec Type
              <select value={priceUpload.sec_type} onChange={(e) => setPriceUpload({ ...priceUpload, sec_type: e.target.value })} style={{ background: '#171a1d', border: '1px solid #384148', color: '#edf5f7', padding: '6px 8px', borderRadius: '4px', font: 'inherit' }}>
                <option value="STK">STK</option>
                <option value="CFD">CFD</option>
                <option value="CASH">CASH</option>
                <option value="IND">IND</option>
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.8rem', color: '#8f9aa0' }}>
              Stock Type
              <select value={priceUpload.stk_type} onChange={(e) => setPriceUpload({ ...priceUpload, stk_type: e.target.value })} style={{ background: '#171a1d', border: '1px solid #384148', color: '#edf5f7', padding: '6px 8px', borderRadius: '4px', font: 'inherit' }}>
                <option value="">Unspecified</option>
                <option value="STOCK">STOCK</option>
                <option value="ETF">ETF</option>
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.8rem', color: '#8f9aa0' }}>
              Data Source
              <input value={priceUpload.data_source} onChange={(e) => setPriceUpload({ ...priceUpload, data_source: e.target.value })} placeholder="Data source" style={{ background: '#171a1d', border: '1px solid #384148', color: '#edf5f7', padding: '6px 8px', borderRadius: '4px', font: 'inherit' }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.8rem', color: '#8f9aa0' }}>
              Timeframe
              <input value={priceUpload.timeframe} onChange={(e) => setPriceUpload({ ...priceUpload, timeframe: e.target.value })} placeholder="Timeframe" style={{ background: '#171a1d', border: '1px solid #384148', color: '#edf5f7', padding: '6px 8px', borderRadius: '4px', font: 'inherit' }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.8rem', color: '#8f9aa0' }}>
              Multiplier
              <input value={priceUpload.multiplier} onChange={(e) => setPriceUpload({ ...priceUpload, multiplier: e.target.value })} placeholder="Multiplier" style={{ background: '#171a1d', border: '1px solid #384148', color: '#edf5f7', padding: '6px 8px', borderRadius: '4px', font: 'inherit' }} />
            </label>
          </div>

          {priceUpload.mode === 'json' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span style={{ fontSize: '0.8rem', color: '#8f9aa0' }}>Bars JSON Array</span>
              <textarea
                style={{ width: '100%', minHeight: '180px', fontFamily: 'monospace', fontSize: '0.85rem', background: '#171a1d', border: '1px solid #384148', color: '#edf5f7', padding: '8px', borderRadius: '4px' }}
                value={priceUpload.barsJson}
                onChange={(e) => setPriceUpload({ ...priceUpload, barsJson: e.target.value })}
                placeholder='[{"timestamp":"2026-01-02T00:00:00","open":100,"high":101,"low":99,"close":100.5,"volume":1000}]'
              />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span style={{ fontSize: '0.8rem', color: '#8f9aa0' }}>Parquet Files</span>
              <input
                type="file"
                multiple
                accept=".parquet,application/octet-stream"
                onChange={(e) => setPriceUploadFiles(Array.from(e.target.files || []))}
                style={{ background: '#171a1d', border: '1px solid #384148', color: '#edf5f7', padding: '8px', borderRadius: '4px', font: 'inherit' }}
              />
              {priceUploadFiles.length > 0 && (
                <span style={{ fontSize: '0.8rem', color: '#8f9aa0' }}>{priceUploadFiles.length} file{priceUploadFiles.length === 1 ? '' : 's'} selected</span>
              )}
            </div>
          )}

          <div className="help-panel subtle" style={{ marginBottom: 0 }}>
            <Info size={16} />
            <div>
              <strong>Format Info</strong>
              <p style={{ margin: 0, fontSize: '0.8rem' }}>{priceUpload.mode === 'json' ? 'Timescale DB upload expects a JSON array with timestamp, open, high, low, close, and optional volume, adjusted_close, metadata_json, indexes, identifiers, and stk_type. The Stock Type selector is used when bars do not include stk_type.' : 'Parquet Lake uploads are stored as Snappy Parquet partitions and queried with DuckDB. Files need timestamp, open, high, low, close, and either a symbol column or the symbol field above. Optional columns include volume, adjusted_close, sec_type, stk_type, exchange, indexes, con_id, metadata_json, FIGI, ISIN, CUSIP, and SEDOL. The Stock Type selector fills stk_type when the file lacks it.'}</p>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button
              onClick={() => setUploadModalOpen(false)}
              style={{ background: 'transparent', border: '1px solid #384148', color: '#edf5f7', cursor: 'pointer', height: '36px', borderRadius: '6px', padding: '0 16px' }}
            >
              Cancel
            </button>
            <button
              onClick={() => void runAction('upload-prices', 'Uploading price data…', async () => {
                await uploadPrices();
                setUploadModalOpen(false);
              })}
              disabled={Boolean(pendingAction)}
              style={{ background: '#20252a', border: '1px solid #384148', color: '#edf5f7', cursor: 'pointer', height: '36px', borderRadius: '6px', padding: '0 16px', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              {pendingAction?.id === 'upload-prices' ? <Loader2 size={14} className="spinner" /> : <Upload size={14} />}
              {pendingAction?.id === 'upload-prices' ? 'Uploading…' : `Upload to ${priceUpload.mode === 'json' ? 'Timescale DB' : 'Parquet Lake'}`}
            </button>
          </div>
        </div>
      </section>
    </div>
  );

  if (!isAuthenticated) {
    return (
      <main className="app-shell locked">
        <aside className="sidebar">
          <div className="brand">
            <Lock className="brand-icon" />
            <div>
              <h1>Research Lake</h1>
              <span>Artifact explorer <small style={{ opacity: 0.6, fontSize: '0.75rem' }}>v{version}</small></span>
            </div>
          </div>
        </aside>
        <section className="workspace empty-workspace">
          <button className="primary-login" onClick={() => setLoginModalOpen(true)}><LogIn size={16} /> Login</button>
        </section>
        {loginModal}
      </main>
    );
  }

  return (
    <main className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <Lock className="brand-icon" />
          <div>
            <h1>Research Lake</h1>
            <span>Artifact explorer <small style={{ opacity: 0.6, fontSize: '0.75rem' }}>v{version}</small></span>
          </div>
        </div>
        <nav>
          {tabs.map(({ id, label, icon: Icon }) => (
            <button key={id} className={tab === id ? 'active' : ''} onClick={() => handleTabChange(id)}>
              <Icon size={16} />
              {label}
            </button>
          ))}
        </nav>
      </aside>

      <section className="workspace" aria-busy={loading || Boolean(pendingAction)}>
        <header className="toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#181d20', borderBottom: '1px solid #2d353b', padding: '10px 16px', borderRadius: '8px 8px 0 0' }}>
          <div className="connection" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <Globe size={14} style={{ position: 'absolute', left: '10px', color: '#8f9aa0' }} />
              <input 
                value={baseUrl} 
                onChange={(e) => setBaseUrl(e.target.value)} 
                aria-label="Research API URL" 
                placeholder="Research API URL"
                style={{ paddingLeft: '30px', fontSize: '0.8rem', height: '32px', minHeight: '32px', background: '#111416', border: '1px solid #31383e', borderRadius: '4px', color: '#edf5f7', width: '220px' }}
              />
            </div>
            
            {!currentUser && (
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <Key size={14} style={{ position: 'absolute', left: '10px', color: '#8f9aa0' }} />
                <input 
                  value={apiKey} 
                  onChange={(e) => setApiKey(e.target.value)} 
                  placeholder="Research API key" 
                  aria-label="Research API key"
                  type="password"
                  style={{ paddingLeft: '30px', fontSize: '0.8rem', height: '32px', minHeight: '32px', background: '#111416', border: '1px solid #31383e', borderRadius: '4px', color: '#edf5f7', width: '200px' }}
                />
              </div>
            )}
          </div>
          <div className="session-bar" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {isAuthenticated ? (
              <div title={sessionTitle} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#20252a', padding: '6px 12px', borderRadius: '20px', border: '1px solid #31383e', fontSize: '0.8rem', color: '#edf5f7' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981', boxShadow: '0 0 8px #10b981' }} />
                <User size={13} style={{ color: '#8f9aa0' }} />
                <span style={{ fontWeight: 500 }}>
                  {currentUser ? `${currentUser.username || currentUser.sub} (${currentUser.role || 'user'})` : 'API Key Access'}
                </span>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#20252a', padding: '6px 12px', borderRadius: '20px', border: '1px solid #31383e', fontSize: '0.8rem', color: '#8f9aa0' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444', boxShadow: '0 0 8px #ef4444' }} />
                <span>No Session</span>
              </div>
            )}
            <button onClick={() => void runAction('refresh', 'Refreshing current view…', load)} disabled={loading || Boolean(pendingAction)} className="icon-button" aria-label={loading ? 'Refreshing' : 'Refresh'} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', minWidth: '32px', padding: 0, background: '#20252a', border: '1px solid #31383e', borderRadius: '4px', color: '#edf5f7', cursor: 'pointer' }}>
              <RefreshCw size={14} className={loading ? 'spinner' : ''} />
            </button>
            {isAuthenticated ? (
              <button 
                onClick={logout} 
                className="icon-button"
                style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '32px', padding: '0 12px', borderRadius: '4px', background: '#991b1b', border: 'none', color: '#ffffff', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500 }}
              >
                <LogOut size={13} /> Sign Out
              </button>
            ) : (
              <button 
                onClick={() => setLoginModalOpen(true)} 
                className="icon-button"
                style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '32px', padding: '0 12px', borderRadius: '4px', background: '#0284c7', border: 'none', color: '#ffffff', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500 }}
              >
                <LogIn size={13} /> Sign In
              </button>
            )}
          </div>
        </header>

        <section className="help-panel">
          <Info size={16} />
          <div>
            <strong>{tabHelp[tab].title}</strong>
            <p>{tabHelp[tab].body}</p>
          </div>
        </section>

        {['backtests', 'optimizations', 'artifacts', 'prices'].includes(tab) && filterOptionsLoading && (
          <div className="inline-loading" role="status" aria-live="polite">
            <Loader2 size={14} className="spinner" /> Loading filter options…
          </div>
        )}
        {['backtests', 'optimizations', 'artifacts', 'prices'].includes(tab) && filterOptionsError && (
          <div className="inline-loading filter-options-error" role="status" aria-live="polite">
            Filter options unavailable: {filterOptionsError}
          </div>
        )}

        <div className="filters">
          {tab === 'backtests' && (
            <>
              <MultiSelect
                label="Symbol"
                shortLabel="Sym"
                value={filters.symbol}
                options={backtestSymbols}
                onChange={(val) => handleFilterChange('symbol', val)}
              />
              <MultiSelect
                label="Strategy"
                shortLabel="Strat"
                value={filters.strategy}
                options={backtestStrategies}
                onChange={(val) => handleFilterChange('strategy', val)}
              />
            </>
          )}
          {tab === 'optimizations' && (
            <>
              <MultiSelect
                label="Symbol"
                shortLabel="Sym"
                value={filters.symbol}
                options={optimizationSymbols}
                onChange={(val) => handleFilterChange('symbol', val)}
              />
              <MultiSelect
                label="Strategy"
                shortLabel="Strat"
                value={filters.strategy}
                options={optimizationStrategies}
                onChange={(val) => handleFilterChange('strategy', val)}
              />
            </>
          )}
          {tab === 'prices' && !selectedSeries && (
            <>
              <CustomSelect
                label="Storage"
                shortLabel="Store"
                value={priceStorageLabel(priceStorage)}
                shortValue={priceStorage === 'lake' ? 'Lake' : 'Timescale'}
                options={['Timescale DB', 'Parquet Lake (DuckDB)']}
                onChange={(val) => setPriceStorage(storageFromLabel(val))}
              />
              <MultiSelect
                label="Symbol"
                shortLabel="Sym"
                value={filters.symbol}
                options={uniqueSymbols}
                onChange={(val) => handleFilterChange('symbol', val)}
              />
              <MultiSelect
                label="Sec type"
                shortLabel="Sec"
                value={filters.sec_type}
                options={uniqueSecTypes}
                onChange={(val) => handleFilterChange('sec_type', val)}
              />
              <MultiSelect
                label="Data source"
                shortLabel="Src"
                value={filters.data_source}
                options={uniqueDataSources}
                onChange={(val) => handleFilterChange('data_source', val)}
              />
              <MultiSelect
                label="Timeframe"
                shortLabel="TF"
                value={filters.timeframe}
                options={uniqueTimeframes}
                onChange={(val) => handleFilterChange('timeframe', val)}
              />
              <MultiSelect
                label="Indexes"
                shortLabel="Idx"
                value={filters.indexes}
                options={uniqueIndexes}
                onChange={(val) => handleFilterChange('indexes', val)}
              />
            </>
          )}
          {tab === 'artifacts' && (
            <>
              <MultiSelect
                label="Artifact type"
                shortLabel="Type"
                value={filters.artifact_type}
                options={artifactTypes}
                onChange={(val) => handleFilterChange('artifact_type', val)}
              />
              <MultiSelect
                label="Tag"
                shortLabel="Tag"
                value={filters.tag}
                options={artifactTags}
                onChange={(val) => handleFilterChange('tag', val)}
              />
            </>
          )}
          
          <div className="filter-actions">
            {Object.values(filters).some(Boolean) && (
              <button 
                onClick={() => {
                  const cleared = { symbol: '', strategy: '', tag: '', artifact_type: '', sec_type: '', data_source: '', timeframe: '', indexes: '' };
                  setFilters(cleared);
                  syncStateToUrl(tab, 1, limit, cleared, tab === 'prices' ? selectedSeries : null);
                }}
                className="icon-button" 
                title="Clear all filters"
                style={{ height: '32px', padding: '0 8px', background: '#20252a', border: '1px solid #7f1d1d', color: '#f87171', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem', fontWeight: 500 }}
              >
                <X size={13} /> Clear
              </button>
            )}
            <MultiSelect
              label="Columns"
              shortLabel="Cols"
              value={activeColumns[tab].join(',')}
              options={ALL_COLUMNS[tab]}
              onChange={(val) => {
                const list = val.split(',').map(s => s.trim()).filter(Boolean);
                setActiveColumns({ ...activeColumns, [tab]: list });
              }}
            />
            {tab === 'prices' && !selectedSeries && (
              <button 
                onClick={() => setUploadModalOpen(true)} 
                className="icon-button" 
                title="Upload price data"
                style={{ height: '32px', padding: '0 8px', background: '#20252a', border: '1px solid #384148', color: '#edf5f7', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.75rem' }}
              >
                <Upload size={13} /> Upload
              </button>
            )}
          </div>
        </div>

        {tab === 'keys' && (
          <>
            <div className="help-panel subtle">
              <Info size={16} />
              <div>
                <strong>Scopes</strong>
                <p>Available scopes: read:artifacts, write:artifacts, delete:artifacts, read:mcp, admin:security. Check the boxes below to select rights.</p>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
              <div className="action-row" style={{ display: 'flex', gap: '10px' }}>
                <input value={keyForm.name} onChange={(e) => setKeyForm({ ...keyForm, name: e.target.value })} placeholder="Key name" />
                <button onClick={() => void runAction('create-key', 'Creating API key…', createKey)} disabled={Boolean(pendingAction)}>
                  {pendingAction?.id === 'create-key' && <Loader2 size={14} className="spinner" />}
                  {pendingAction?.id === 'create-key' ? 'Creating…' : 'Create Key'}
                </button>
                {createdKey && <code className="created-key">{createdKey}</code>}
              </div>
              <ScopeSelector currentScopes={keyForm.scopes} onChange={(scopes) => setKeyForm({ ...keyForm, scopes })} />
            </div>
          </>
        )}

        {tab === 'users' && (
          <>
            <div className="help-panel subtle">
              <Info size={16} />
              <div>
                <strong>User grants</strong>
                <p>User ID must match an existing app user. Research grants are lake-specific and do not change trading permissions.</p>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
              <div className="action-row" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <input value={userForm.user_id} onChange={(e) => setUserForm({ ...userForm, user_id: e.target.value })} placeholder="User ID" />
                <input value={userForm.username} onChange={(e) => setUserForm({ ...userForm, username: e.target.value })} placeholder="Username" />
                <input value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} placeholder="Password / reset" type="password" />
                <input value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })} placeholder="Role" />
                <button onClick={() => void runAction('save-user', 'Saving research user…', saveUser)} disabled={Boolean(pendingAction)}>
                  {pendingAction?.id === 'save-user' && <Loader2 size={14} className="spinner" />}
                  {pendingAction?.id === 'save-user' ? 'Saving…' : 'Save User'}
                </button>
              </div>
              <ScopeSelector currentScopes={userForm.scopes} onChange={(scopes) => setUserForm({ ...userForm, scopes })} />
            </div>
          </>
        )}

        {tab === 'mcp' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
            <div className="action-row" style={{ display: 'flex', gap: '10px' }}>
              <input value={mcpForm.client_name} onChange={(e) => setMcpForm({ ...mcpForm, client_name: e.target.value })} placeholder="Client name" />
              <button onClick={() => void runAction('create-mcp-grant', 'Creating MCP grant…', createMcpGrant)} disabled={Boolean(pendingAction)}>
                {pendingAction?.id === 'create-mcp-grant' && <Loader2 size={14} className="spinner" />}
                {pendingAction?.id === 'create-mcp-grant' ? 'Creating…' : 'Create MCP Grant'}
              </button>
            </div>
            <ScopeSelector currentScopes={mcpForm.scopes} onChange={(scopes) => setMcpForm({ ...mcpForm, scopes })} />
          </div>
        )}


        {error && <div className="error">{error}</div>}
        {pendingAction && (
          <div className="action-progress" role="status" aria-live="polite">
            <Loader2 size={15} className="spinner" />
            <span>{pendingAction.label}</span>
          </div>
        )}
        {loading && (
          <div className="loading" role="status" aria-live="polite">
            <Loader2 size={16} className="spinner loading-spinner" aria-hidden="true" />
            <div>
              <strong>Loading {tabs.find((item) => item.id === tab)?.label || 'Research Lake'}</strong>
              <span>{loadingStatus || 'Waiting for research lake...'}</span>
            </div>
          </div>
        )}

        {tab === 'prices' && selectedSeries ? (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', background: '#1e293b', padding: '12px', borderRadius: '8px', border: '1px solid #334155' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button 
                  onClick={handleCloseSeries}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', fontSize: '0.85rem', cursor: 'pointer', background: '#334155', border: 'none', borderRadius: '4px', color: '#f8fafc', fontWeight: 500 }}
                  title="Return to Price Inventory"
                >
                  <ArrowLeft size={14} /> Back to Inventory
                </button>
                <h2 style={{ fontSize: '1.05rem', fontWeight: 600, color: '#38bdf8', margin: 0 }}>
                  Series: {selectedSeries.symbol} ({selectedSeries.timeframe}) · {selectedSeries.data_source}{selectedSeries.data_type && selectedSeries.data_type !== 'TRADES' ? ` · ${selectedSeries.data_type}` : ''} · {selectedSeries.sec_type} (mult: {selectedSeries.multiplier})
                </h2>
              </div>
              {!selectedSeriesIsVolatility && <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <CustomSelect
                  label="Storage"
                  value={selectedSeries.storage === 'lake' ? 'Parquet Lake (DuckDB)' : 'Timescale DB'}
                  options={['Timescale DB', 'Parquet Lake (DuckDB)']}
                  onChange={(val) => {
                    const nextStorage = val === 'Parquet Lake (DuckDB)' ? 'lake' : 'timescale';
                    const nextSeries = { ...selectedSeries, storage: nextStorage };
                    setSelectedSeries(nextSeries);
                    syncStateToUrl('prices', page, limit, filters, nextSeries);
                  }}
                />
              </div>}
              {selectedSeriesIsVolatility && <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Timescale DB · values shown as percent</span>}
            </div>
            
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{selectedSeriesIsVolatility ? 'Session Date' : 'Timestamp'}</th>
                    <th>Open</th>
                    <th>High</th>
                    <th>Low</th>
                    <th>Close</th>
                    {!selectedSeriesIsVolatility && <>
                      <th>Volume</th>
                      <th>Adjusted Close</th>
                      <th>Con ID</th>
                      <th>Stk Type</th>
                      <th>Indexes</th>
                      <th>Exchange</th>
                      <th>Metadata</th>
                    </>}
                  </tr>
                </thead>
                <tbody>
                  {items.map((bar, idx) => (
                    <tr key={idx}>
                      <td>{selectedSeriesIsVolatility ? String(bar.timestamp || '').slice(0, 10) : formatDate(bar.timestamp)}</td>
                      <td>{formatSelectedSeriesValue(bar.open)}</td>
                      <td>{formatSelectedSeriesValue(bar.high)}</td>
                      <td>{formatSelectedSeriesValue(bar.low)}</td>
                      <td>{formatSelectedSeriesValue(bar.close)}</td>
                      {!selectedSeriesIsVolatility && <>
                        <td>{metric(bar.volume)}</td>
                        <td>{metric(bar.adjusted_close)}</td>
                        <td>{bar.con_id ?? '-'}</td>
                        <td>{bar.stk_type ?? '-'}</td>
                        <td>{Array.isArray(bar.indexes) ? bar.indexes.join(', ') : (bar.indexes ?? '-')}</td>
                        <td>{bar.exchange ?? '-'}</td>
                        <td>{bar.metadata_json ? (typeof bar.metadata_json === 'string' ? bar.metadata_json : JSON.stringify(bar.metadata_json)) : '-'}</td>
                      </>}
                    </tr>
                  ))}
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={selectedSeriesIsVolatility ? 5 : 12} style={{ textAlign: 'center', color: '#94a3b8', padding: '24px' }}>
                        {loading ? <span className="empty-loading"><Loader2 size={14} className="spinner" /> Loading market data…</span> : 'No observations found in this series.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination 
              page={page} 
              limit={limit} 
              total={total} 
              onChange={handlePageChange} 
              onLimitChange={(newLimit) => {
                syncStateToUrl(tab, 1, newLimit, filters, selectedSeries);
              }}
            />
          </div>
        ) : tab === 'audit' ? (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Time</th><th>Actor</th><th>Action</th><th>Resource</th><th>Detail</th></tr></thead>
              <tbody>
                {audit.map((row) => (
                  <tr key={row.id}>
                    <td>{formatDate(row.created_at)}</td>
                    <td>{row.actor_type}:{row.actor_id}</td>
                    <td>{row.action}</td>
                    <td>{row.resource_type}:{row.resource_id}</td>
                    <td>{row.detail || '-'}</td>
                  </tr>
                ))}
                {audit.length === 0 && (
                  <tr><td colSpan={5} className="empty-cell">
                    {loading ? <span className="empty-loading"><Loader2 size={14} className="spinner" /> Loading audit events…</span> : 'No audit events found.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div>
            {canBulkDelete && selectedRowIds.size > 0 && (
              <div className="bulk-actions-bar">
                <span style={{ color: '#7dd3fc', fontSize: '0.85rem', fontWeight: 500 }}>
                  {allFilteredSelected
                    ? `All ${selectedRowIds.size} in filtered view selected`
                    : `${selectedRowIds.size} selected`}
                </span>
                {total > selectedRowIds.size && (
                  <button
                    disabled={Boolean(pendingAction) || selectAllLoading}
                    onClick={() => void selectAllFiltered()}
                    title={`Select all ${total} items matching current filters`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      height: '28px',
                      padding: '0 10px',
                      fontSize: '0.8rem',
                      background: '#0284c7',
                      color: '#ffffff',
                      border: '1px solid #38bdf8',
                      borderRadius: '4px',
                      cursor: pendingAction || selectAllLoading ? 'not-allowed' : 'pointer',
                      fontWeight: 500,
                    }}
                  >
                    {selectAllLoading ? <Loader2 size={13} className="spinner" /> : <CheckSquare size={13} />}
                    Select all {total} in filtered view
                  </button>
                )}
                <button
                  className="danger"
                  disabled={Boolean(pendingAction) || selectAllLoading}
                  onClick={() => void runAction(`bulk-delete-${tab}`, `Deleting ${selectedRowIds.size} selected ${tab === 'prices' ? 'price series' : tab}…`, () => deleteSelectedRows(tab))}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', height: '28px', padding: '0 10px', fontSize: '0.8rem' }}
                >
                  {pendingAction?.id === `bulk-delete-${tab}` ? <Loader2 size={13} className="spinner" /> : <Trash2 size={13} />}
                  Delete selected ({selectedRowIds.size})
                </button>
                <button
                  disabled={Boolean(pendingAction) || selectAllLoading}
                  onClick={() => updateSelectedRows(tab, Array.from(selectedRowIds), false)}
                  style={{ background: 'transparent', border: '1px solid #334155', borderRadius: '4px', color: '#cbd5e1', padding: '0 10px', height: '28px', fontSize: '0.8rem', cursor: pendingAction || selectAllLoading ? 'not-allowed' : 'pointer' }}
                >
                  Clear selection
                </button>
              </div>
            )}
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    {canBulkDelete && (
                      <th style={{ width: '48px', textAlign: 'center', position: 'relative' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', justifyContent: 'center' }}>
                          <input
                            type="checkbox"
                            ref={headerCheckboxRef}
                            checked={allFilteredSelected || (pageRowIds.length > 0 && allPageRowsSelected)}
                            disabled={(pageRowIds.length === 0 && total === 0) || loading || Boolean(pendingAction) || selectAllLoading}
                            aria-label={allFilteredSelected ? "Deselect all" : allPageRowsSelected ? "Select all in view" : "Select page"}
                            title={allFilteredSelected ? "Deselect all" : allPageRowsSelected ? `Select all ${total} in view` : `Select page (${pageRowIds.length})`}
                            onChange={handleHeaderCheckboxToggle}
                            style={{ cursor: 'pointer', accentColor: '#0284c7', margin: 0 }}
                          />
                          <button
                            type="button"
                            disabled={loading || Boolean(pendingAction) || selectAllLoading || total === 0}
                            onClick={(e) => {
                              e.stopPropagation();
                              setHeaderMenuOpen(prev => !prev);
                            }}
                            title="Selection options"
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: '2px',
                              margin: 0,
                              cursor: (loading || total === 0) ? 'not-allowed' : 'pointer',
                              color: '#94a3b8',
                              display: 'inline-flex',
                              alignItems: 'center',
                              lineHeight: 1,
                            }}
                          >
                            <ChevronDown size={11} />
                          </button>
                        </div>
                        {headerMenuOpen && (
                          <div
                            ref={headerMenuRef}
                            className="custom-select-options"
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              zIndex: 100,
                              minWidth: '180px',
                              textAlign: 'left',
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div
                              className="custom-select-option"
                              style={{ padding: '6px 12px', fontSize: '0.8rem', cursor: 'pointer', whiteSpace: 'nowrap' }}
                              onClick={() => {
                                setHeaderMenuOpen(false);
                                updateSelectedRows(tab, pageRowIds, true);
                              }}
                            >
                              Select page ({pageRowIds.length})
                            </div>
                            <div
                              className="custom-select-option"
                              style={{ padding: '6px 12px', fontSize: '0.8rem', cursor: 'pointer', color: '#38bdf8', fontWeight: 600, whiteSpace: 'nowrap' }}
                              onClick={() => {
                                setHeaderMenuOpen(false);
                                void selectAllFiltered();
                              }}
                            >
                              Select all in view ({total})
                            </div>
                            {selectedRowIds.size > 0 && (
                              <>
                                <div style={{ borderBottom: '1px solid #334155', margin: '2px 0' }} />
                                <div
                                  className="custom-select-option"
                                  style={{ padding: '6px 12px', fontSize: '0.8rem', cursor: 'pointer', color: '#f87171', whiteSpace: 'nowrap' }}
                                  onClick={() => {
                                    setHeaderMenuOpen(false);
                                    updateSelectedRows(tab, Array.from(selectedRowIds), false);
                                  }}
                                >
                                  Clear selection
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </th>
                    )}
                    {visibleColumns.map((column) => {
                      const isSorted = sortConfig?.key === column;
                      const icon = isSorted ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : '';
                      return (
                        <th 
                          key={column} 
                          onClick={() => handleSort(column)}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                        >
                          {column === 'data_source' ? 'src' : column.replace(/_/g, ' ')}{icon}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((item, index) => {
                    const rowId = getRowId(tab, item);
                    const isSelected = selectedRowIds.has(rowId);
                    const isSelectable = Boolean(rowId && !(tab === 'keys' && rowId.startsWith('env-')));
                    const isRowAction = tab === 'prices';
                    const isClickable = isRowAction || canBulkDelete;

                    const handleRowClick = () => {
                      if (isRowAction) {
                        handleSelectSeries(item);
                      } else if (canBulkDelete && isSelectable) {
                        updateSelectedRows(tab, [rowId], !isSelected);
                      }
                    };

                    return (
                      <tr 
                        key={rowId || `${tab}-${index}`}
                        className={`${isClickable ? 'clickable-row' : ''} ${isSelected ? 'selected-row' : ''}`}
                        onClick={handleRowClick}
                      >
                        {canBulkDelete && (
                          <td style={{ width: '36px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={!isSelectable || Boolean(pendingAction)}
                              aria-label={`Select ${tab.slice(0, -1)} ${rowId}`}
                              onChange={event => updateSelectedRows(tab, [rowId], event.target.checked)}
                              style={{ cursor: isSelectable ? 'pointer' : 'not-allowed', accentColor: '#0284c7' }}
                            />
                          </td>
                        )}
                        {visibleColumns.map((column) => <td key={column}>{renderCell(item, column)}</td>)}
                      </tr>
                    );
                  })}
                  {sortedItems.length === 0 && (
                    <tr>
                      <td colSpan={visibleColumns.length + (canBulkDelete ? 1 : 0)} style={{ textAlign: 'center', color: '#94a3b8', padding: '24px' }}>
                        {loading ? <span className="empty-loading"><Loader2 size={14} className="spinner" /> Loading {tabs.find((item) => item.id === tab)?.label.toLowerCase() || 'results'}…</span> : tab === 'prices' && priceStorage === 'lake'
                          ? `No lake price inventory found${priceLakePath ? ` at ${priceLakePath}` : ''}.`
                          : `No ${tab} found.`}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {((['backtests', 'portfolios', 'optimizations', 'artifacts', 'prices'].includes(tab) && (tab !== 'prices' || !selectedSeries))) && (
              <Pagination 
                page={page} 
                limit={limit} 
                total={total} 
                onChange={handlePageChange} 
                onLimitChange={(newLimit) => {
                  syncStateToUrl(tab, 1, newLimit, filters, tab === 'prices' ? selectedSeries : null);
                }}
              />
            )}
          </div>
        )}
      </section>
      {loginModal}
      {uploadModal}
    </main>
  );
}

createRoot(document.getElementById('root')!).render(<App />);
